import pricingEngineConfig from 'app/api/pricingEngineConfig.json';

function getPricingEngineBaseUrl(): string {
    if (import.meta.env.DEV) {
        return '/pricing-engine-api';
    }
    
    const useProxy = import.meta.env.VITE_PRICING_ENGINE_USE_PROXY === 'true';
    if (useProxy && typeof window !== 'undefined') {
        return '/pricing-engine-api';
    }
    if (typeof window !== 'undefined' && window.location.hostname) {
        const hostname = window.location.hostname.toLowerCase();
        if (hostname.includes('pricing-admin-staging') || hostname.includes('staging')) {
            return pricingEngineConfig.stagingUrl;
        }
        if (hostname.includes('pricing-admin-test') || hostname.includes('test')) {
            return pricingEngineConfig.testUrl;
        }
        if (hostname.includes('pricing-admin-dev') || hostname.includes('dev')) {
            return pricingEngineConfig.devUrl;
        }
    }
    
    const env = import.meta.env.MODE || 'development';
    if (env === 'test' || env === 'testing') {
        return pricingEngineConfig.testUrl;
    }
    if (env === 'staging') {
        return pricingEngineConfig.stagingUrl;
    }
    //TODO: add production environment
    return pricingEngineConfig.devUrl;
}

function buildPricingEngineHeaders(apiKey: string, includeContentType = false): Record<string, string> {
    const headers: Record<string, string> = {
        'Accept': 'application/json',
    };

    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    if (apiKey) {
        headers['X-ApiKey'] = apiKey;
    } else {
        throw new Error('API key is required for Pricing Engine API requests');
    }

    //headers['Cache-Control'] = 'no-cache';
    //headers['Pragma'] = 'no-cache';
    return headers;
}

export function getBookingSystemApiKey(bookingSystemName: string): string | undefined {
    return pricingEngineConfig.bookingSystemApiKeys[bookingSystemName as keyof typeof pricingEngineConfig.bookingSystemApiKeys];
}

export interface ReferenceDataItem {
    id: string | number;
    name: string;
    code?: string;
}

export interface Country extends ReferenceDataItem {
    numericId?: number;
}

export interface Currency extends ReferenceDataItem {
    symbol?: string;
}

export interface Centre extends ReferenceDataItem {
    countryCode?: string;
    currencyCode?: string;
}

export interface School extends ReferenceDataItem {
    centreCode?: string;
    countryCode?: string;
    currencyCode?: string;
}

export interface BookingSystem extends ReferenceDataItem {
    apiKey?: string;
}

async function fetchPricingEngineApi<T>(
    endpoint: string,
    errorMessage: string,
    apiKey: string,
    parser?: (item: any, index?: number) => T
): Promise<T[]> {
    const baseUrl = getPricingEngineBaseUrl();
    let url: string | undefined = `${baseUrl}${endpoint}`;
    const allItems: any[] = [];
    
    try {
        if (!apiKey) {
            throw new Error('API key is required for Pricing Engine API requests');
        }
        const headers = buildPricingEngineHeaders(apiKey);
        
        while (url !== undefined && url !== null) {
            const response: Response = await fetch(url, {
                method: 'GET',
                headers,
                cache: 'no-store',
                mode: 'cors',
                credentials: 'omit',
                referrerPolicy: 'no-referrer',
            });

            if (!response.ok) {
                const text = await response.text().catch(() => '');
                throw new Error(`${errorMessage} (HTTP ${response.status}) — ${text || response.statusText}`);
            }

            const data: any = await response.json();
            
            let items: any[] = [];
            if (data && Array.isArray(data.Results)) {
                items = data.Results;
            } else if (Array.isArray(data)) {
                items = data;
            } else if (data && Array.isArray(data.data)) {
                items = data.data;
            } else if (data && Array.isArray(data.items)) {
                items = data.items;
            } else if (data && typeof data === 'object') {
                items = [data];
            }
            
            allItems.push(...items);
            
            if (data && data.NextPage) {
                try {
                    const nextPageUrl: URL = new URL(data.NextPage);
                    const pathAndQuery: string = nextPageUrl.pathname + nextPageUrl.search;
                    url = `${baseUrl}${pathAndQuery}`;
                } catch (e) {
                    if (data.NextPage.startsWith('/')) {
                        url = `${baseUrl}${data.NextPage}`;
                    } else if (data.NextPage.startsWith('http')) {
                        const match = data.NextPage.match(/https?:\/\/[^\/]+(\/.*)/);
                        url = match ? `${baseUrl}${match[1]}` : undefined;
                    } else {
                        url = `${baseUrl}/${data.NextPage}`;
                    }
                }
            } else if (data && data.NextPageId) {
                const endpointPath = endpoint.includes('?') ? endpoint.split('?')[0] : endpoint;
                url = `${baseUrl}${endpointPath}?pageId=${encodeURIComponent(data.NextPageId)}`;
            } else {
                url = undefined;
            }
        }
        
        const items = allItems;

        if (parser) {
            const parsed = items.map((item, index) => parser(item, index));
            return parsed;
        }
        return items as T[];
    } catch (error: any) {
        if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
            throw new Error(`${errorMessage}: Network error or CORS issue. Please check if the Pricing Engine API is accessible.`);
        }
        throw error;
    }
}

async function callPricingEngineApi<T>(
    endpoint: string,
    method: string,
    apiKey: string,
    body?: any,
    errorMessage?: string
): Promise<T> {
    const baseUrl = getPricingEngineBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    
    try {
        if (!apiKey) {
            throw new Error('API key is required for Pricing Engine API requests');
        }
        const headers = buildPricingEngineHeaders(apiKey, !!body);

        const response = await fetch(url, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
            cache: 'no-store',
        });

        if (response.status === 409) {
            const text = await response.text().catch(() => '');
            if (text) {
                try {
                    const errorResponse = JSON.parse(text);
                    if (errorResponse.Products !== undefined || errorResponse.InvalidProducts !== undefined) {
                        return errorResponse as T;
                    }
                } catch (e) {
                }
            }
        }

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`${errorMessage || 'API request failed'} (HTTP ${response.status}) — ${text || response.statusText}`);
        }

        if (response.status === 204) {
            return null as T;
        }

        return await response.json();
    } catch (error: any) {
        if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
            throw new Error(`${errorMessage || 'Request failed'}: Network error or CORS issue. Please check if the Pricing Engine API is accessible.`);
        }
        throw error;
    }
}

export async function getPricingEngineCountries(apiKey: string): Promise<Country[]> {
    return fetchPricingEngineApi<Country>(
        '/v1/product/reference-data?WithDataTypes=Country',
        'Unable to fetch countries from Pricing Engine',
        apiKey,
        (dto: any, index = 0) => ({
            id: dto.Code || dto.code || dto.id || dto.countryId || `country-${index}`,
            name: dto.Name || dto.name || dto.countryName || `Country ${index + 1}`,
            code: dto.Code || dto.code || dto.countryCode,
            numericId: typeof dto.id === 'number' ? dto.id : typeof dto.countryId === 'number' ? dto.countryId : undefined,
        })
    );
}

export async function getPricingEngineCurrencies(apiKey: string): Promise<Currency[]> {
    return fetchPricingEngineApi<Currency>(
        '/v1/product/reference-data?WithDataTypes=Currency',
        'Unable to fetch currencies from Pricing Engine',
        apiKey,
        (dto: any, index = 0) => ({
            id: dto.Code || dto.code || dto.id || dto.currencyId || `currency-${index}`,
            name: dto.Name || dto.name || dto.currencyName || `Currency ${index + 1}`,
            code: dto.Code || dto.code || dto.currencyCode,
            symbol: dto.Symbol || dto.symbol || dto.currencySymbol,
        })
    );
}

function extractCurrencyCode(val: any): string | undefined {
    if (!val) return undefined;
    if (typeof val === 'string') {
        const s = val.trim();
        if (/^[A-Z]{3}$/i.test(s)) return s.toUpperCase();
        const match = /\(([A-Za-z]{3})\)/.exec(s);
        return match ? match[1].toUpperCase() : undefined;
    }
    if (typeof val === 'object') {
        const code = val.Code || val.code || val.currencyCode || val.CurrencyCode;
        return code ? String(code).trim().toUpperCase() : undefined;
    }
    return undefined;
}

export async function getPricingEngineCentres(apiKey: string): Promise<Centre[]> {
    return fetchPricingEngineApi<Centre>(
        '/v1/product/reference-data?WithDataTypes=Centre',
        'Unable to fetch centres from Pricing Engine',
        apiKey,
        (dto: any, index = 0) => {
            const currencyCode =
                dto.CurrencyCode || dto.currencyCode ||
                extractCurrencyCode(dto.Currency) ||
                extractCurrencyCode(dto.currency);
            return {
                id: dto.Code || dto.code || dto.id || dto.centreId || `centre-${index}`,
                name: dto.Name || dto.name || dto.centreName || `Centre ${index + 1}`,
                code: dto.Code || dto.code || dto.centreCode,
                countryCode: dto.CountryCode || dto.countryCode,
                currencyCode,
            };
        }
    );
}

export function parseDivisionIdToNumber(value: string | number | undefined | null): number | undefined {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'number') {
        if (!Number.isFinite(value) || value <= 0) return undefined;
        return Math.trunc(value);
    }
    const s = String(value).trim();
    if (!s) return undefined;
    const withoutPrefix = s.replace(/^division-/i, '');
    const n = Number(withoutPrefix);
    if (!Number.isNaN(n) && Number.isFinite(n) && n > 0) return Math.trunc(n);
    const tail = s.match(/(\d+)$/);
    if (tail) {
        const p = parseInt(tail[1], 10);
        if (p > 0) return p;
    }
    return undefined;
}

export async function getPricingEngineDivisions(apiKey: string): Promise<ReferenceDataItem[]> {
    return fetchPricingEngineApi<ReferenceDataItem>(
        '/v1/product/reference-data?WithDataTypes=Division',
        'Unable to fetch divisions from Pricing Engine',
        apiKey,
        (dto: any, index = 0) => {
            const raw = dto.Id ?? dto.id ?? dto.divisionId;
            const num = parseDivisionIdToNumber(raw);
            return {
                id: num != null ? String(num) : String(index + 1),
                name: dto.Name ?? dto.name ?? dto.divisionName ?? `Division ${index + 1}`,
            };
        }
    );
}

export async function getPricingEngineSchools(apiKey: string): Promise<School[]> {
    const endpoints = [
        '/v1/product/schools',
        '/v1/product/reference-data?WithDataTypes=School'
    ];
    
    for (const endpoint of endpoints) {
        try {
            const result = await fetchPricingEngineApi<School>(
                endpoint,
                'Unable to fetch schools from Pricing Engine',
                apiKey,
                (dto: any, index = 0) => {
                    const id = dto.Code || dto.code || dto.SchoolCode || dto.schoolCode
                        || dto.id || dto.schoolId || dto.Id || `school-${index}`;
                    
                    const name = dto.name || dto.Name || dto.schoolName || dto.SchoolName || dto.school || dto.School || dto.title || dto.Title || dto.displayName || dto.DisplayName;
                    
                    const currencyCode = dto.CurrencyCode || dto.currencyCode ||
                        extractCurrencyCode(dto.Currency) || extractCurrencyCode(dto.currency);
                    
                    return {
                        id: id,
                        name: name || `School ${index + 1}`,
                        code: dto.code || dto.Code || dto.schoolCode || dto.SchoolCode || String(id),
                        centreCode: dto.centreCode || dto.CentreCode || (dto.Centre && dto.Centre.Code) || (dto.centre && dto.centre.code) || undefined,
                        countryCode: dto.countryCode || dto.CountryCode || (dto.Country && dto.Country.Code) || (dto.country && dto.country.code) || undefined,
                        currencyCode,
                    };
                }
            );
            
            if (result && result.length > 0) {
                return result;
            }
        } catch (error) {
            continue;
        }
    }
    
    return [];
}

export interface ProductOfferingMinimal {
    schoolId: string;
    productId: string;
    name?: string;
    numericProductId?: number;
}

function toPricingApiProductType(productType: string): string {
    switch (productType) {
        case 'Add-ons': return 'AddOn';
        case 'Course':
        case 'Accommodation':
        case 'Transfer':
        case 'Package':
        default: return productType;
    }
}

function toPositiveInt(value: unknown): number | undefined {
    if (value == null || value === '') return undefined;
    if (typeof value === 'object') {
        const inner = (value as { id?: unknown; Id?: unknown }).id
            ?? (value as { id?: unknown; Id?: unknown }).Id;
        return toPositiveInt(inner);
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
    return Math.trunc(parsed);
}

export async function getProductOfferingsFromPricingApi(
    schoolCodes: string[],
    productType: string,
    divisionId: number,
    apiKey?: string,
    noOfWeeks?: number,
    withDatesAfter?: string,
    withDatesBefore?: string
): Promise<ProductOfferingMinimal[]> {
    const key = apiKey ?? getBookingSystemApiKey('API');
    if (!key) {
        throw new Error('API key required for Pricing API. Configure bookingSystemApiKeys.API in pricingEngineConfig.');
    }
    const withSchools = schoolCodes.map(s => String(s).replace(/^School-/i, '').trim()).filter(Boolean).join(',');
    if (!withSchools) {
        return [];
    }
    const withProductTypes = toPricingApiProductType(productType);
    const params = new URLSearchParams({
        IncludeProducts: 'true',
        IncludePrices: 'true',
        WithSchools: withSchools,
        WithProductTypes: withProductTypes,
        DivisionId: String(divisionId),
    });
    if (noOfWeeks != null && noOfWeeks > 0) {
        params.set('NoOfWeeks', String(noOfWeeks));
    }
    const datesAfter = withDatesAfter?.trim();
    if (datesAfter) {
        params.set('WithDatesAfter', datesAfter);
    }
    const datesBefore = withDatesBefore?.trim();
    if (datesBefore) {
        params.set('WithDatesBefore', datesBefore);
    }
    const endpoint = `/v2/product/offerings?${params.toString()}`;
    const raw = await fetchPricingEngineApi<any>(
        endpoint,
        'Unable to fetch product offerings from Pricing API',
        key
    );
    const result: ProductOfferingMinimal[] = [];
    for (const item of raw) {
        const product = item.Product ?? item.product;
        // Accommodation must use Product.Code (legacy codes e.g. R001/H001).
        const productId =
            withProductTypes === 'Accommodation'
                ? (product && (product.Code ?? product.code))
                  ?? item.ProductCode ?? item.productCode
                  ?? item.ProductId ?? item.productId
                  ?? (product && (product.Id ?? product.id))
                  ?? item.Id ?? item.id
                : item.ProductCode ?? item.productCode ?? item.ProductId ?? item.productId
                  ?? (product && (product.Code ?? product.code ?? product.Id ?? product.id))
                  ?? item.Id ?? item.id;
        const schoolId = item.SchoolCode ?? item.schoolCode ?? item.SchoolId ?? item.schoolId
            ?? (item.School && (item.School.Code ?? item.School.code))
            ?? (item.school && (item.school.Code ?? item.school.code));
        if (productId != null && schoolId != null) {
            const name = item.Name ?? item.name ?? item.OfferingName ?? item.offeringName
                ?? (product && (product.Name ?? product.name));
            const numericProductId = toPositiveInt(
                product?.Id ?? product?.id
                ?? (typeof item.ProductId === 'number' ? item.ProductId : undefined)
                ?? (typeof item.productId === 'number' ? item.productId : undefined)
            );
            result.push({
                schoolId: String(schoolId),
                productId: String(productId),
                ...(name != null && name !== '' && { name: String(name) }),
                ...(numericProductId != null && { numericProductId }),
            });
        }
    }
    return result;
}

export interface CalculatePriceRequest {
    Options: {
        SkipEarlyBird: boolean;
        SkipMarketDiscounts: boolean;
        SkipCommission: boolean;
        PromotionCode?: string;
    };
    BookingDetails: {
        BookingCreationDate: string;
        BookingChangeDate?: string | null;
        SaleCountryIsoCode: string;
        AgentId?: number | null;
        AgentOfficeId?: number | null;
        StudentDateOfBirth?: string;
        StudentHasArrived?: boolean | null;
        CurrencyCode?: string;
    };
    PriceLockToken?: string;
    Products: Array<{
        DivisionId: number;
        SchoolCode: string;
        ProductCode: string;
        Quantity: {
            StartDate: string;
            EndDate: string;
            NumberOfUnits: number;
            UnitType: string;
        };
    }>;
}

export interface CalculatePriceResponse {
    Products?: Array<{
        SchoolCode: string;
        ProductCode: string;
        Name: string;
        BasePrice: string;
        DiscountTotal: string;
        TaxAmount: string;
        Commission: string;
        DatePeriods?: Array<{
            StartDate: string;
            EndDate: string;
            PriceBeforeDiscounts: string;
            PriceAfterDiscounts: string;
            DiscountTotal: string;
            Tax: string;
            TaxCode: string;
            Discounts?: Array<{
                DiscountValue: string;
                DiscountCode: string;
                DiscountName: string;
                DiscountType: string;
                ChangeStatus?: string;
            }>;
        }>;
        Discounts?: Array<{
            DiscountCode: string;
            Name: string;
            Type: string;
            DiscountAmount: string;
            Dates?: Array<{
                From: string;
                To: string;
            }>;
        }>;
        ProductDeliveryDates?: {
            From: string;
            To: string;
        };
        BreakdownLines?: Array<{
            Percentage?: number | string;
            Type?: string;
            OfferingName?: string;
            Name?: string;
            LineTotal?: string;
        }>;
    }>;
    InvalidProducts?: Array<{
        Name: string;
        SchoolCode: string;
        ProductCode: string;
        ErrorMessage: string;
    }>;
    Fees?: Array<{
        NumberOfUnits: any;
        FeeCode: string;
        Name: string;
        BasePrice: string;
        DiscountTotal: string;
        TaxAmount: string;
        Commission: string;
        DatePeriods?: Array<{
            StartDate: string;
            EndDate: string;
            PriceBeforeDiscounts: string;
            PriceAfterDiscounts: string;
            TaxCode?: string;
            PricePerUnit?: {
                PricePerMajorUnit?: string;
                PricePerMinorUnit?: string;
            };
            Discounts?: Array<{
                DiscountValue: string;
                DiscountCode: string;
                DiscountName: string;
                DiscountType: string;
                ChangeStatus?: string;
            }>;
        }>;
        Discounts?: Array<{
            DiscountCode: string;
            Name: string;
            Type: string;
            DiscountAmount: string;
            Dates?: Array<{ From: string; To: string }>;
        }>;
    }>;
    BasePrice?: string;
    TaxAmount?: string;
    DiscountTotal?: string;
    CommissionTotal?: string;
    PriceLockToken?: string;
    AppliedPromotionCode?: string;
    PromotionCode?: string;
    Warnings?: Array<{
        Message?: string;
    }>;
    ResponseStatus?: {
        ErrorCode?: string;
        Message?: string;
        Errors?: Array<{
            ErrorCode?: string;
            FieldName?: string;
            Message?: string;
        }>;
    };
}

export async function calculatePrice(
    request: CalculatePriceRequest,
    apiKey: string
): Promise<CalculatePriceResponse> {
    return callPricingEngineApi<CalculatePriceResponse>(
        '/v1/calculate-price',
        'POST',
        apiKey,
        request,
        'Unable to calculate price'
    );
}